import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { useMyOrganizations, useViewer } from "@/hooks/useOrganizations";
import { useClaimBadgeWhenComplete } from "@/hooks/useBadges";
import { orgBadgeKind } from "@/lib/badges";
import { ROUTES } from "@/lib/constants";
import { useOrgHasPostedQuest, useOrgHasSentMessage, useOrgHasUploadedFile, useOrgPolls } from "@/hooks/useOrgHub";
import { isPollOpen } from "@/lib/orgHub";
import type { MyOrganization } from "@/lib/organizations";
import {
  buildOrgChecklist,
  orgChecklistCandidates,
  orgChecklistTier,
  pickOrgForRing,
  type OrgChecklist,
  type OrgChecklistTier,
} from "./orgChecklist";
import {
  completedSeenFlag,
  hiddenFlag,
  NO_ORG_FLAGS,
  orgFlagsVersion,
  readOrgFlags,
  setOrgFlag,
  subscribeOrgFlags,
  type OrgChecklistFlag,
} from "./orgChecklistStorage";

export type OrgGettingStarted =
  /** `hiddenByMember`: they pressed Hide at this level — the hub offers "Show" again. */
  | { status: "hidden"; hiddenByMember: boolean; tier: OrgChecklistTier | null }
  | { status: "loading" }
  | { status: "ready"; tier: OrgChecklistTier; checklist: OrgChecklist; completedSeen: boolean };

/**
 * The org checklist for the viewer, at their level, or "hidden" when they
 * aren't a member or hid it at this level. The contributor probes only run
 * for Medio and up. A failed probe reads as "loading" — the card is a nudge,
 * so it stays out of the way rather than show an error on the org's landing
 * page (and never claims a step done that it couldn't check).
 */
export function useOrgGettingStarted(org: MyOrganization): OrgGettingStarted {
  const gs = useOrgChecklistState(org);
  // Finishing the list at your level earns its badge (a Medio's earns both).
  useClaimBadgeWhenComplete(
    orgBadgeKind(gs.status === "ready" ? gs.tier : "simple"),
    org.id,
    gs.status === "ready" && gs.checklist.complete,
  );
  return gs;
}

function useOrgChecklistState(org: MyOrganization): OrgGettingStarted {
  const viewer = useViewer();
  const tier = viewer ? orgChecklistTier(org.myRole) : null;
  const flags = useSyncExternalStore(subscribeOrgFlags, () =>
    viewer ? readOrgFlags(viewer, org.id) : NO_ORG_FLAGS,
  );
  const enabled = tier !== null && !flags[hiddenFlag(tier)];
  const contributor = enabled && tier === "contributor";
  const sent = useOrgHasSentMessage(org.id, enabled);
  const polls = useOrgPolls(org.id, enabled);
  const uploaded = useOrgHasUploadedFile(org.id, contributor);
  const posted = useOrgHasPostedQuest(org.id, contributor);

  if (!enabled) return { status: "hidden", hiddenByMember: tier !== null, tier };
  if (sent.data === undefined || polls.data === undefined) return { status: "loading" };
  if (contributor && (uploaded.data === undefined || posted.data === undefined)) return { status: "loading" };

  const now = Date.now();
  const checklist = buildOrgChecklist(
    {
      hasSentMessage: sent.data,
      visitedBoard: flags.visitedBoard,
      visitedMembers: flags.visitedMembers,
      hasVoted: polls.data.some((p) => p.myVote !== null),
      hasOpenPoll: polls.data.some((p) => isPollOpen(p, now)),
    },
    contributor
      ? {
          hasUploadedFile: uploaded.data === true,
          // org_poll_list returns the newest 100 the viewer may see; a Medio's
          // admins-only poll isn't among them, so that one doesn't count.
          hasCreatedPoll: polls.data.some((p) => p.createdBy === viewer),
          hasPostedQuest: posted.data === true,
          hasInvited: flags.invited,
        }
      : null,
  );
  return { status: "ready", tier, checklist, completedSeen: flags[completedSeenFlag(tier)] };
}

/** Set (or, for un-hiding, clear) one of the viewer's flags for this org (no-op while signed out). */
export function useOrgChecklistFlag(orgId: string): (flag: OrgChecklistFlag, value?: boolean) => void {
  const viewer = useViewer();
  return useCallback(
    (flag, value = true) => {
      if (viewer) setOrgFlag(viewer, orgId, flag, value);
    },
    [viewer, orgId],
  );
}

/**
 * Which org the top-bar ring shows, and the others it could switch to. The
 * org whose pages are open wins until the member picks another in the
 * popover; moving to another page resets the pick.
 */
export function useOrgChecklistRingTarget() {
  const viewer = useViewer();
  const mine = useMyOrganizations();
  const { pathname } = useLocation();
  const routeSlug =
    matchPath(ROUTES.ORG_MANAGE, pathname)?.params.slug ?? matchPath(ROUTES.ORG, pathname)?.params.slug ?? null;
  const version = useSyncExternalStore(subscribeOrgFlags, orgFlagsVersion);
  const [picked, setPicked] = useState<string | null>(null);
  useEffect(() => setPicked(null), [routeSlug]);

  const candidates = useMemo(
    () => (viewer ? orgChecklistCandidates(mine.data ?? [], (id) => readOrgFlags(viewer, id)) : []),
    // version: a flag flip (Hide, all set seen) changes who is a candidate.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- version is the re-read trigger for readOrgFlags, not an input
    [viewer, mine.data, version],
  );
  return { org: pickOrgForRing(candidates, routeSlug, picked), candidates, pick: setPicked };
}
